import { c as createFocusTrap } from './focus-trap.esm-C-N_-G0z.js';
import { P as PubSub } from './pubsub-CoPYzvVm.js';
import './notyf-DIw0RvBj.js';

/**
 * Lock the body scroll
 * @param {boolean} boolean - The boolean to lock the body scroll
 */
function bodyScrollLock(boolean = true) {
  const verticalScrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

  if (verticalScrollbarWidth <= 0 && boolean) return;

  document.body.style.paddingRight = boolean ? `${verticalScrollbarWidth}px` : '';
  document.documentElement.style.setProperty(
    '--scrollbar-width',
    boolean ? `${verticalScrollbarWidth}px` : ''
  );
  document.body.style.overflow = boolean ? 'hidden' : '';
}

/* eslint-disable wc/define-tag-after-class-definition, wc/no-constructor */


class DialogExt extends HTMLElement {
  #focusDelay = this.dataset.focusDelay || 300;
  #preventFocusScroll = this.dataset.preventFocusScroll === 'true';
  #bodyElement = this.querySelector('[data-body]');
  #focusContainer = this.querySelector('[data-focus-container]') || this;
  #trapFocus = createFocusTrap(this.#focusContainer, {
    allowOutsideClick: true,
    clickOutsideDeactivates: (event) => this.#clickOutsideDeactivates(event),
    checkCanFocusTrap: () => this.#checkCanFocusTrap(),
    onActivate: () => this.#onActivate(),
    onDeactivate: () => this.#onDeactivate(),
    onPostDeactivate: () => this.#onPostDeactivate(),
    preventScroll: this.#preventFocusScroll,
  });

  /** @type {boolean} */ #lockedByThemeEditor = false;
  /** @type {HTMLElement[]} */ controllers = [
    ...document.querySelectorAll(`[aria-controls="${this.id}"]`),
  ];
  /** @type {HTMLElement} */ target = this.controllers?.[0]?.ariaControlsElements?.[0];

  constructor() {
    super();
    this.#teleport();
    this.#handleConstructorErrors();
    this.#handlePubSubEvents();
    this.#handleShopifyEvents();

    this.controllers.forEach((controller) => {
      controller.addEventListener('click', (event) => {
        event.preventDefault();
        this.ariaHidden === 'true' ? this.open() : this.close();
      });
    });
  }

  open() {
    if (this.ariaHidden === 'false') return;
    this.#trapFocus.activate();
  }

  close() {
    if (this.ariaHidden === 'true') return;
    this.#trapFocus.deactivate();
  }

  /**
   * @returns Promise<void>
   */
  #checkCanFocusTrap() {
    return new Promise((resolve) => setTimeout(() => resolve(), this.#focusDelay));
  }

  /**
   * @param {MouseEvent} event - The event to check if the click is outside the dialog
   * @returns {boolean} - Whether the click is outside the dialog
   */
  #clickOutsideDeactivates({ target }) {
    if (this.dataset.preventDismiss === 'true') return false;
    return !this.controllers.some((controller) => controller.contains(target));
  }

  #handleConstructorErrors() {
    if (!this.id) console.error('DialogExt must have an id');
    if (!this.controllers.length) console.error('DialogExt must have at least one controller');
    if (!this.target) console.error('DialogExt must have a target');
  }

  #onActivate() {
    bodyScrollLock();
    this.ariaHidden = 'false';
    this.controllers.forEach((controller) => (controller.ariaExpanded = 'true'));
  }

  #onDeactivate() {
    this.ariaHidden = 'true';
    this.controllers.forEach((controller) => (controller.ariaExpanded = 'false'));
  }

  #onPostDeactivate() {
    const onTransitionEnd = ({ target }) => {
      if (target !== this.#bodyElement) return;
      bodyScrollLock(false);
      this.#bodyElement.removeEventListener('transitionend', onTransitionEnd);
    };
    this.#bodyElement.addEventListener('transitionend', onTransitionEnd);
  }

  #teleport() {
    if (this.dataset.teleport !== 'true') return;

    this.removeAttribute('data-teleport');
    document.body.insertBefore(this, document.body.firstChild);
  }

  #handlePubSubEvents() {
    PubSub.subscribe('dialog:open', ({ id }) => {
      if (id !== this.id) return;
      this.open();
    });
    PubSub.subscribe('dialog:close', ({ id }) => {
      if (id !== this.id) return;
      this.close();
    });
  }

  #handleShopifyEvents() {
    const events = [
      'shopify:section:load',
      'shopify:section:select',
      'shopify:section:unload',
      'shopify:section:deselect',
    ];

    events.forEach((eventName) => {
      document.addEventListener(eventName, ({ detail, type }) => {
        const { sectionId } = detail;
        if (sectionId !== this.id) return;

        const openActions = ['shopify:section:load', 'shopify:section:select'];
        this.#lockedByThemeEditor = openActions.includes(type);
        this.#lockedByThemeEditor ? this.open() : this.close();
      });
    });
  }
}

export { DialogExt as D };
